import clsx from 'clsx';
import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  CircleStackIcon,
  EnvelopeIcon,
  FunnelIcon,
  PhoneIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { AnimatePresence } from 'motion/react';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { DataTable } from '@oysterjs/ui/data-table';
import React from 'react';
import {
  AdminAnalyticsActivityType,
  AdminAnalyticsReportFieldsFragment,
  AdminAnalyticsSection,
  AdminAnalyticsTimeSavingsCategory
} from '~/api/graphql';
import { getAdminAnalyticsReport } from '~/api/data/analytics/queries';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { titleCase } from '~/components/workspaces/lib/utils';
import {
  ACTIVITY_LABELS,
  dateFormatter,
  dateTimeFormatter,
  formatNumber,
  longDateFormatter,
  VISIBLE_PERIODS
} from '~/components/settings/analytics/analytics-constants';
import {
  ACTIVITY_PALETTE,
  STAT_CARD_ACCENT_CLASSES,
  TEMPLATE_BADGE_TO_PROGRESS,
  WORKSPACE_PALETTE,
  type StatCardAccent,
  type TemplateBadgeColor
} from '~/components/settings/analytics/analytics-tokens';
import { type AnalyticsAssumptions } from '~/components/settings/analytics/analytics-assumptions-picker';
import {
  type AnalyticsProfileOption,
  profileAdoptionSummary,
  unfilteredSectionNote
} from './analytics-segment-filters';
import {
  DeletedPresetFootnote,
  EmptyMessage,
  FilterNote,
  KpiTile,
  presetDisplayLabel,
  SectionHeading
} from '~/components/settings/analytics/analytics-card-parts';
import { AnalyticsFilterChips } from '~/components/settings/analytics/analytics-filter-chips';
import { AmsWritesSection } from '~/components/settings/analytics/ams-writes-section';
import {
  CollapsedLegendPopover,
  type CollapsedLegendEntry,
  CustomPresetTag
} from '~/components/settings/analytics/collapsed-legend-popover';
import { MetricProgressBar } from '~/components/settings/analytics/metric-progress-bar';
import { PresetRankingsSection } from '~/components/settings/analytics/preset-rankings-section';
import {
  computeIncrementalRevenueDollars,
  effectiveAssumptions,
  formatCloseLikelihoodPercent
} from '~/components/settings/analytics/incremental-revenue';
import { useAnalyticsFilters } from '~/components/settings/analytics/use-analytics-filters';
import {
  getUserActivityColumns,
  sortUserActivity,
  type UserActivitySortKey
} from '~/components/settings/analytics/user-activity-columns';

type Report = AdminAnalyticsReportFieldsFragment;

const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1
});
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

export const SettingsAnalyticsPage = () => {
  const filterProps = useAnalyticsFilters();
  const { filter: currentFilter, hasSegmentFilter, segmentFiltersEnabled } = filterProps;
  const [report, setReport] = React.useState<Report | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [retryKey, setRetryKey] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);
    getAdminAnalyticsReport(currentFilter)
      .then((result) => {
        if (cancelled) return;
        if (result.error) {
          // Surface GraphQL failures with the same retry UI as network failures.
          setLoadError(true);
        } else if (result.data) {
          setReport(result.data);
        }
        // A null/undefined result is a genuine empty load → EmptyState.
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentFilter, retryKey]);

  return (
    <div className="space-y-5">
      <AnalyticsFilterChips {...filterProps} />
      <AnimatePresence>
        {isLoading ? (
          <DashboardSkeleton />
        ) : loadError ? (
          <ErrorState onRetry={() => setRetryKey((k) => k + 1)} />
        ) : report ? (
          <AnalyticsDashboard
            report={report}
            assumptions={filterProps.assumptions}
            segmentFiltersEnabled={segmentFiltersEnabled}
            // Kept apart from a real 0, which the dashboard shows when no filter narrows it.
            matchesNoActivity={segmentFiltersEnabled && hasSegmentFilter && hasNoActivity(report)}
            selectedProfileOption={filterProps.selectedProfileOption}
          />
        ) : (
          <EmptyState />
        )}
      </AnimatePresence>
    </div>
  );
};

// totalActivities leaves out AMS writes, which are counted on their own.
const hasNoActivity = (report: Report) =>
  report.totals.totalActivities === 0 && report.amsWrites.succeeded + report.amsWrites.failed === 0;

const EmptyState = () => (
  <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-6 py-16 text-center">
    <ChartBarIcon className="mx-auto h-10 w-10 text-neutral-400 dark:text-neutral-500" />
    <h3 className="mt-3 text-sm font-semibold text-neutral-900 dark:text-white">
      No analytics data
    </h3>
    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
      We could not load product usage for this view. Try a wider date range.
    </p>
  </div>
);

const NoMatchingActivityState = () => (
  <div
    role="status"
    className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-6 py-16 text-center"
  >
    <FunnelIcon className="mx-auto h-10 w-10 text-neutral-400 dark:text-neutral-500" />
    <h3 className="mt-3 text-sm font-semibold text-neutral-900 dark:text-white">
      No activity matches these filters
    </h3>
    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
      Try a wider date range, or clear a filter above.
    </p>
  </div>
);

// Tickets are never user-filterable, so their note shows regardless of the segment filters gate.
const UNGATED_SECTIONS = new Set([AdminAnalyticsSection.RevenueServiceTickets]);

/** The note shown in place of a section the filters can't apply to, if any. */
function unfilteredNote(
  report: Report,
  section: AdminAnalyticsSection,
  segmentFiltersEnabled: boolean
): string | undefined {
  if (!segmentFiltersEnabled && !UNGATED_SECTIONS.has(section)) return undefined;
  const unfiltered = report.unfilteredSections.find((entry) => entry.section === section);
  return unfiltered ? unfilteredSectionNote(unfiltered) : undefined;
}

const TIME_SAVINGS_SECTIONS: Partial<
  Record<AdminAnalyticsTimeSavingsCategory, AdminAnalyticsSection>
> = {
  [AdminAnalyticsTimeSavingsCategory.Call]: AdminAnalyticsSection.Calls,
  [AdminAnalyticsTimeSavingsCategory.Email]: AdminAnalyticsSection.Emails
};

const CHART_SECTIONS: Partial<Record<AdminAnalyticsActivityType, AdminAnalyticsSection>> = {
  [AdminAnalyticsActivityType.Call]: AdminAnalyticsSection.Calls,
  [AdminAnalyticsActivityType.Email]: AdminAnalyticsSection.Emails
};

const ErrorState = ({ onRetry }: { onRetry: () => void }) => (
  <div className="rounded-2xl border border-dashed border-rose-200 dark:border-rose-500/30 bg-rose-50/40 dark:bg-rose-500/10 px-6 py-16 text-center">
    <ChartBarIcon className="mx-auto h-10 w-10 text-rose-400" />
    <h3 className="mt-3 text-sm font-semibold text-rose-900 dark:text-rose-200">
      Couldn't load analytics
    </h3>
    <p className="mt-1 text-sm text-rose-700 dark:text-rose-300">
      Something went wrong loading product usage. Please try again.
    </p>
    <Button type="button" color="white" className="mt-4" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

const DashboardSkeleton = () => (
  <div className="space-y-6">
    <div className="h-44 animate-pulse rounded-2xl bg-neutral-200 dark:bg-neutral-700" />
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-28 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-700" />
      ))}
    </div>
    <div className="h-80 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-700" />
    <div className="grid gap-6 xl:grid-cols-2">
      <div className="h-64 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-700" />
      <div className="h-64 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-700" />
    </div>
    <div className="h-48 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-700" />
    <div className="h-72 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-700" />
    <div className="h-64 animate-pulse rounded-xl bg-neutral-200 dark:bg-neutral-700" />
  </div>
);

const AnalyticsDashboard = ({
  report,
  assumptions,
  segmentFiltersEnabled,
  matchesNoActivity,
  selectedProfileOption
}: {
  report: Report;
  assumptions: AnalyticsAssumptions;
  segmentFiltersEnabled: boolean;
  matchesNoActivity: boolean;
  selectedProfileOption: AnalyticsProfileOption | undefined;
}) => {
  const periods = React.useMemo(() => buildActivityPeriods(report), [report]);
  const trends = React.useMemo(() => computeTrends(periods), [periods]);
  const showAmsWrites = useFlag(FEATURE_FLAGS.ANALYTICS_AMS_WRITES);
  const showPresetRankings = useFlag(FEATURE_FLAGS.ANALYTICS_PRESET_RANKINGS);

  const noteFor = (section: AdminAnalyticsSection) =>
    unfilteredNote(report, section, segmentFiltersEnabled);
  const isUnfiltered = (section: AdminAnalyticsSection | undefined) =>
    section != null && noteFor(section) != null;
  const unfilteredChartTypes = new Set(
    Object.values(AdminAnalyticsActivityType).filter((type) => isUnfiltered(CHART_SECTIONS[type]))
  );
  const activeUsersNote = noteFor(AdminAnalyticsSection.ActiveUsers);

  const teamPerformance = (
    <TeamPerformanceSection
      report={report}
      activeUsersNote={activeUsersNote}
      adoptionSummary={
        selectedProfileOption && !activeUsersNote
          ? profileAdoptionSummary(
              selectedProfileOption,
              report.totals.activeUsers,
              report.totals.totalUsers
            )
          : undefined
      }
    />
  );
  const updatedAt = (
    <p className="text-right text-xs text-neutral-400 dark:text-neutral-500">
      Updated at {dateTimeFormatter.format(toDate(report.generatedAt))}
    </p>
  );

  // Adoption has its own fixed window, so it stays when nothing in the date range matches.
  if (matchesNoActivity) {
    return (
      <div className="space-y-6">
        <NoMatchingActivityState />
        {teamPerformance}
        {updatedAt}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HeroBanner
        report={report}
        assumptions={assumptions}
        serviceTicketsNote={noteFor(AdminAnalyticsSection.RevenueServiceTickets)}
        isUnfiltered={isUnfiltered}
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Workspaces"
          value={report.totals.totalWorkspaces}
          subtitle="tasks Cara worked on"
          icon={<SparklesIcon className="size-5" />}
          accent="violet"
          trend={trends.workspace}
        />
        <StatCard
          title="Calls"
          value={report.totals.totalCalls}
          subtitle="voice activity"
          icon={<PhoneIcon className="size-5" />}
          accent="primary"
          trend={trends.call}
          unfilteredNote={noteFor(AdminAnalyticsSection.Calls)}
        />
        <StatCard
          title="Emails"
          value={report.totals.totalEmails}
          subtitle="inbound messages"
          icon={<EnvelopeIcon className="size-5" />}
          accent="secondary"
          trend={trends.email}
          unfilteredNote={noteFor(AdminAnalyticsSection.Emails)}
        />
        <StatCard
          title="Knowledge"
          value={report.totals.totalKnowledgeBasePages}
          subtitle={`pages indexed · ${formatNumber(report.totals.totalKnowledgeBaseUploads)} docs`}
          icon={<CircleStackIcon className="size-5" />}
          accent="accent"
          unfilteredNote={noteFor(AdminAnalyticsSection.KnowledgeBaseUploads)}
        />
      </div>

      <Card>
        <ActivityAreaChart
          periods={periods}
          unfilteredTypes={unfilteredChartTypes}
          kbDocumentHistory={report.kbDocumentHistory}
          kbHistoryNote={noteFor(AdminAnalyticsSection.KnowledgeBaseHistory)}
        />
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <WorkspaceMix report={report} />
        </Card>
        <Card>
          <WorkspaceInsightsCard report={report} />
        </Card>
      </div>

      {showPresetRankings && (
        <Card>
          <PresetRankingsSection presetRankings={report.presetRankings} />
        </Card>
      )}

      {showAmsWrites && (
        <Card>
          <AmsWritesSection
            amsWrites={report.amsWrites}
            voiceAiNote={noteFor(AdminAnalyticsSection.VoiceAiAmsWrites)}
          />
        </Card>
      )}

      <TemplateBreakdownSection
        report={report}
        libraryNote={noteFor(AdminAnalyticsSection.TemplateLibrary)}
      />

      <div className="pt-6 border-t border-neutral-100 dark:border-neutral-700">
        <h2 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-white">
          Knowledge base insights
        </h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Upload and search activity for your team&apos;s knowledge library.
        </p>
      </div>

      <Card>
        <KnowledgeBaseInsightsPanel
          report={report}
          uploadsNote={noteFor(AdminAnalyticsSection.KnowledgeBaseUploads)}
        />
      </Card>

      {teamPerformance}

      {updatedAt}
    </div>
  );
};

const TeamPerformanceSection = ({
  report,
  activeUsersNote,
  adoptionSummary
}: {
  report: Report;
  activeUsersNote: string | undefined;
  adoptionSummary: string | undefined;
}) => (
  <>
    <div className="pt-6 border-t border-neutral-100 dark:border-neutral-700">
      <h2 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-white">
        Team performance and adoption
      </h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        Active users are defined by {report.totals.activeDefinition}. This section always covers
        that fixed window, regardless of the date range selected above.
      </p>
      {adoptionSummary && (
        <p className="mt-1 text-xs font-medium text-neutral-700 dark:text-neutral-300">
          {adoptionSummary}
        </p>
      )}
    </div>

    <Card>
      <UserActivityPanel report={report} adoptionNote={activeUsersNote} />
    </Card>
  </>
);

const Card = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div
    className={`rounded-xl bg-white dark:bg-neutral-800 px-5 py-5 shadow sm:px-6 ${className ?? ''}`}
  >
    {children}
  </div>
);

// HeroBanner deliberately uses blue-950/blue-900/sky-600 rather than primary tokens.
// This is the one place we want to stand out from the rest of the settings chrome.
const HeroBanner = ({
  report,
  assumptions,
  serviceTicketsNote,
  isUnfiltered
}: {
  report: Report;
  assumptions: AnalyticsAssumptions;
  serviceTicketsNote: string | undefined;
  isUnfiltered: (section: AdminAnalyticsSection | undefined) => boolean;
}) => {
  const after = report.dateRange.after ? toDate(report.dateRange.after) : null;
  const before = report.dateRange.before ? toDate(report.dateRange.before) : null;
  const { timeSavings } = report;
  const { hourlyRate, avgAccountPremium } = effectiveAssumptions(assumptions);

  return (
    <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-blue-950 via-blue-900 to-sky-600 shadow-lg">
      <div className="flex flex-col gap-6 px-6 py-7 text-white sm:px-8">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-blue-100">
            <SparklesIcon className="size-4" />
            Admin analytics
          </div>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl lg:leading-tight">
            Here&apos;s what Cara did for your team{' '}
            {after && before
              ? `between ${dateFormatter.format(after)} and ${dateFormatter.format(before)}`
              : 'across the selected period'}
            .
          </h2>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <CaraActivitiesHeroPanel report={report} />
          <HeroMetricPanel
            label="Estimated time saved"
            value={formatTimeSaved(timeSavings.totalMinutes)}
            detail="based on activity counts"
            breakdown={timeSavings.breakdown
              .filter((row) => !isUnfiltered(TIME_SAVINGS_SECTIONS[row.category]))
              .map((row) => ({
                label: `${formatNumber(row.activityCount)} ${row.label.toLowerCase()}`,
                value: formatTimeSaved(row.minutes)
              }))}
          />
          <AdditionalRevenueHeroPanel
            report={report}
            hourlyRate={hourlyRate}
            avgAccountPremium={avgAccountPremium}
            serviceTicketsNote={serviceTicketsNote}
          />
        </div>
      </div>
    </div>
  );
};

const AdditionalRevenueHeroPanel = ({
  report,
  hourlyRate,
  avgAccountPremium,
  serviceTicketsNote
}: {
  report: Report;
  hourlyRate: number;
  avgAccountPremium: number;
  serviceTicketsNote: string | undefined;
}) => {
  const { timeSavings, incrementalRevenue } = report;
  const timeSavedDollars = (timeSavings.totalMinutes / 60) * hourlyRate;
  const incrementalRevenueDollars = computeIncrementalRevenueDollars(
    incrementalRevenue,
    avgAccountPremium
  );
  const combinedDollars = timeSavedDollars + incrementalRevenueDollars;

  const expectedAccounts = incrementalRevenue.expectedIncrementalAccounts;
  const accountsLabel =
    expectedAccounts >= 0.95 && expectedAccounts < 10
      ? `~${expectedAccounts.toFixed(1)}`
      : formatNumber(Math.round(expectedAccounts));

  const breakdownRows = React.useMemo(() => {
    const rows: { label: string; value: string }[] = [];
    rows.push({ label: 'Value of Time Saved', value: formatCurrency(timeSavedDollars) });
    rows.push({
      label: 'Incremental revenue',
      value: formatCurrency(incrementalRevenueDollars)
    });
    if (incrementalRevenue.clientsConsidered > 0) {
      rows.push({
        label: 'Clients worked',
        value: formatNumber(incrementalRevenue.clientsConsidered)
      });
      rows.push({
        label: 'Additional expected accounts',
        value: `${accountsLabel} (avg ${formatCloseLikelihoodPercent(incrementalRevenue.avgCloseLikelihood)} lift)`
      });
    }
    return rows;
  }, [
    timeSavedDollars,
    incrementalRevenueDollars,
    incrementalRevenue.clientsConsidered,
    incrementalRevenue.avgCloseLikelihood,
    accountsLabel
  ]);

  const revenueDetail = `${formatCurrency(timeSavedDollars)} + ${formatCurrency(incrementalRevenueDollars)}`;

  return (
    <HeroMetricPanel
      label="Estimated Revenue Generated"
      value={formatCurrency(combinedDollars)}
      detail={revenueDetail}
      breakdown={breakdownRows}
      note={serviceTicketsNote}
    />
  );
};

const CaraActivitiesHeroPanel = ({ report }: { report: Report }) => (
  <HeroMetricPanel
    label="Cara activities"
    value={formatNumber(report.totals.totalActivities)}
    detail="workspaces, emails, and uploads"
    breakdown={[
      { label: 'Pages read', value: formatNumber(report.totals.totalPagesReviewed) },
      {
        label: 'Documents generated',
        value: formatNumber(report.totals.totalTemplateReferences)
      },
      { label: 'Workspaces created', value: formatNumber(report.totals.totalWorkspaces) }
    ]}
  />
);

const HeroMetricPanel = ({
  label,
  value,
  detail,
  breakdown,
  note
}: {
  label: string;
  value: string;
  detail?: string;
  breakdown: { label: string; value: string }[];
  note?: string;
}) => (
  <div className="flex h-full flex-col rounded-2xl bg-white/10 px-5 py-4 ring-1 ring-white/15">
    <div className="text-[10px] font-medium uppercase tracking-wider text-blue-100">{label}</div>
    <div className="mt-1 text-4xl font-semibold leading-none">{value}</div>
    {detail != null && detail !== '' && <div className="mt-1 text-xs text-blue-200">{detail}</div>}
    {breakdown.length > 0 && (
      <div className="mt-3 space-y-1.5 border-t border-white/10 pt-3">
        {breakdown.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-4 text-[11px]">
            <span className="text-blue-200">{row.label}</span>
            <span className="text-right font-medium text-white">{row.value}</span>
          </div>
        ))}
      </div>
    )}
    {note && <p className="mt-3 text-[11px] text-blue-200">{note}</p>}
  </div>
);

type Trend = { current: number; previous: number; deltaPct: number | null };
type ActivityPeriod = {
  periodStart: Date;
  total: number;
  counts: Partial<Record<AdminAnalyticsActivityType, number>>;
};

const StatCard = ({
  title,
  value,
  subtitle,
  icon,
  accent,
  trend,
  unfilteredNote
}: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ReactNode;
  accent: StatCardAccent;
  trend?: Trend;
  /** Shown in place of the numbers when the filters can't apply to them. */
  unfilteredNote?: string;
}) => {
  return (
    <div className="rounded-xl bg-white dark:bg-neutral-800 px-5 py-4 shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {title}
          </dt>
          {unfilteredNote ? (
            <dd className="mt-1">
              <FilterNote>{unfilteredNote}</FilterNote>
            </dd>
          ) : (
            <>
              <dd className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900 dark:text-white">
                {formatNumber(value)}
              </dd>
              <dd className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{subtitle}</dd>
            </>
          )}
        </div>
        <div className={`rounded-lg p-2 ${STAT_CARD_ACCENT_CLASSES[accent]}`}>{icon}</div>
      </div>
      {!unfilteredNote && trend && trend.deltaPct !== null && (
        <div className="mt-3 flex items-center gap-1.5 text-xs">
          {trend.deltaPct >= 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-700 dark:text-emerald-300">
              <ArrowTrendingUpIcon className="size-3" />
              {Math.abs(trend.deltaPct)}%
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 dark:bg-rose-500/10 px-2 py-0.5 font-medium text-rose-700 dark:text-rose-300">
              <ArrowTrendingDownIcon className="size-3" />
              {Math.abs(trend.deltaPct)}%
            </span>
          )}
          <span className="text-neutral-500 dark:text-neutral-400">vs. prior half of range</span>
        </div>
      )}
    </div>
  );
};

// Stacking order for the area chart: largest/most-familiar types at the bottom.
// KB is excluded here — it appears as a cumulative line on the right axis.
const AREA_STACK_ORDER: AdminAnalyticsActivityType[] = [
  AdminAnalyticsActivityType.Workspace,
  AdminAnalyticsActivityType.Call,
  AdminAnalyticsActivityType.Email
];

const ActivityAreaChart = ({
  periods,
  unfilteredTypes,
  kbDocumentHistory,
  kbHistoryNote
}: {
  periods: ActivityPeriod[];
  unfilteredTypes: ReadonlySet<AdminAnalyticsActivityType>;
  kbDocumentHistory: { periodStart: Date | string; documentCount: number }[];
  kbHistoryNote?: string;
}) => {
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null);

  // Each activity type is independently togglable by clicking its legend pill.
  const [hidden, setHidden] = React.useState<Set<AdminAnalyticsActivityType>>(() => new Set());
  const toggle = (type: AdminAnalyticsActivityType) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });

  const visiblePeriods = periods.slice(-VISIBLE_PERIODS);

  // These useMemo calls must stay above the early return — React rules of hooks
  // require every hook to be called unconditionally on every render.
  const kbHistoryMap = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const s of kbDocumentHistory) {
      m.set(toDate(s.periodStart).toISOString().slice(0, 10), s.documentCount);
    }
    return m;
  }, [kbDocumentHistory]);

  const kbDocCounts = React.useMemo(() => {
    let lastKnown = 0;
    return visiblePeriods.map((p) => {
      const key = p.periodStart.toISOString().slice(0, 10);
      const val = kbHistoryMap.get(key);
      if (val !== undefined) lastKnown = val;
      return lastKnown;
    });
  }, [visiblePeriods, kbHistoryMap]);

  if (visiblePeriods.length === 0) {
    return (
      <ChartFrame title="Activity over time" subtitle="Click a type to show or hide it">
        <p className="py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">
          No activity for this filter.
        </p>
      </ChartFrame>
    );
  }

  const width = 800;
  const height = 240;
  const paddingLeft = 40;
  const paddingRight = 46; // room for right axis (KB cumulative)
  const paddingTop = 16;
  const paddingBottom = 28;
  const innerWidth = width - paddingLeft - paddingRight;
  const innerHeight = height - paddingTop - paddingBottom;
  const xStep =
    visiblePeriods.length === 1 ? innerWidth / 2 : innerWidth / (visiblePeriods.length - 1);

  const toX = (i: number) => paddingLeft + i * xStep;

  // ── Left axis: stacked area (workspace / call / email) ──────────────────
  const areaTypes = AREA_STACK_ORDER.filter((t) => !unfilteredTypes.has(t));
  const activeAreaTypes = areaTypes.filter((t) => !hidden.has(t));

  const stacked = visiblePeriods.map((period) => {
    let cumulative = 0;
    const entries = areaTypes.map((type) => {
      const count = hidden.has(type) ? 0 : (period.counts[type] ?? 0);
      const start = cumulative;
      cumulative += count;
      return { type, start, end: cumulative };
    });
    return { period, total: cumulative, entries };
  });

  const maxTotal = Math.max(1, ...stacked.map((s) => s.total));
  const toY = (value: number) => paddingTop + innerHeight - (value / maxTotal) * innerHeight;

  const layerPaths = activeAreaTypes.map((type) => {
    const top = stacked.map((s, i) => {
      const entry = s.entries.find((e) => e.type === type)!;
      return { x: toX(i), y: toY(entry.end) };
    });
    const bottom = stacked
      .map((s, i) => {
        const entry = s.entries.find((e) => e.type === type)!;
        return { x: toX(i), y: toY(entry.start) };
      })
      .reverse();
    if (top.every((p) => p.y === toY(0))) return { type, d: '', visible: false };
    const d = [
      `M ${top[0].x} ${top[0].y}`,
      ...top.slice(1).map((p) => `L ${p.x} ${p.y}`),
      ...bottom.map((p) => `L ${p.x} ${p.y}`),
      'Z'
    ].join(' ');
    return { type, d, visible: true };
  });

  const gridYs = [0.25, 0.5, 0.75, 1].map((ratio) => ({
    y: paddingTop + innerHeight * (1 - ratio),
    label: Math.round(maxTotal * ratio)
  }));

  // ── Right axis: KB document count line ──────────────────────────────────
  const showKbLine = !kbHistoryNote && !hidden.has(AdminAnalyticsActivityType.KnowledgeBaseUpload);
  const maxKb = Math.max(1, ...kbDocCounts);
  const toYKb = (value: number) => paddingTop + innerHeight - (value / maxKb) * innerHeight;
  const kbLinePath =
    kbDocCounts.length === 0
      ? ''
      : [
          `M ${toX(0)} ${toYKb(kbDocCounts[0])}`,
          ...kbDocCounts.slice(1).map((v, i) => `L ${toX(i + 1)} ${toYKb(v)}`)
        ].join(' ');
  const kbGridYs = [0.5, 1].map((ratio) => ({
    y: paddingTop + innerHeight * (1 - ratio),
    label: Math.round(maxKb * ratio)
  }));

  const labelIndices =
    visiblePeriods.length <= 6
      ? visiblePeriods.map((_, i) => i)
      : [0, Math.floor(visiblePeriods.length / 2), visiblePeriods.length - 1];

  return (
    <ChartFrame
      title="Activity over time"
      subtitle="Click a type to show or hide it"
      legend={
        <div className="flex flex-wrap justify-end gap-1.5">
          {areaTypes.map((type) => {
            const active = !hidden.has(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggle(type)}
                className={clsx(
                  'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                  !active &&
                    'border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500'
                )}
                style={
                  active
                    ? {
                        backgroundColor: ACTIVITY_PALETTE[type].soft,
                        borderColor: ACTIVITY_PALETTE[type].hex + '66',
                        color: '#1F2937'
                      }
                    : { backgroundColor: 'transparent' }
                }
              >
                <span
                  className="size-2 shrink-0 rounded-full transition-all"
                  style={{ backgroundColor: active ? ACTIVITY_PALETTE[type].hex : '#D1D5DB' }}
                />
                {ACTIVITY_LABELS[type]}
              </button>
            );
          })}
          {/* KB chip — dashed icon hints at line style */}
          {(() => {
            if (kbHistoryNote) return null;
            const kbType = AdminAnalyticsActivityType.KnowledgeBaseUpload;
            const active = !hidden.has(kbType);
            return (
              <button
                type="button"
                onClick={() => toggle(kbType)}
                className={clsx(
                  'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                  !active &&
                    'border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500'
                )}
                style={
                  active
                    ? {
                        backgroundColor: ACTIVITY_PALETTE[kbType].soft,
                        borderColor: ACTIVITY_PALETTE[kbType].hex + '66',
                        color: '#1F2937'
                      }
                    : { backgroundColor: 'transparent' }
                }
              >
                <svg width="12" height="5" className="shrink-0">
                  <line
                    x1="0"
                    y1="2.5"
                    x2="12"
                    y2="2.5"
                    stroke={active ? ACTIVITY_PALETTE[kbType].hex : '#D1D5DB'}
                    strokeWidth="1.5"
                    strokeDasharray="3 2"
                  />
                </svg>
                {ACTIVITY_LABELS[kbType]}
              </button>
            );
          })()}
        </div>
      }
    >
      <div className="relative">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full aspect-[10/3]"
          onMouseLeave={() => setHoverIndex(null)}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const relativeX = ((e.clientX - rect.left) / rect.width) * width - paddingLeft;
            const idx = Math.round(relativeX / xStep);
            const clamped = Math.max(0, Math.min(visiblePeriods.length - 1, idx));
            setHoverIndex(clamped);
          }}
        >
          {/* Left-axis grid lines */}
          {gridYs.map((g) => (
            <g key={g.y}>
              <line
                x1={paddingLeft}
                x2={width - paddingRight}
                y1={g.y}
                y2={g.y}
                className="stroke-neutral-200 dark:stroke-neutral-700"
                strokeDasharray="3 3"
              />
              <text
                x={paddingLeft - 6}
                y={g.y + 3}
                textAnchor="end"
                fontSize="8"
                className="fill-neutral-400 dark:fill-neutral-500"
              >
                {compactNumberFormatter.format(g.label)}
              </text>
            </g>
          ))}

          {/* Stacked area layers */}
          {layerPaths.map(
            (layer) =>
              layer.visible && (
                <path
                  key={layer.type}
                  d={layer.d}
                  fill={ACTIVITY_PALETTE[layer.type].hex}
                  fillOpacity={0.85}
                  className="stroke-white dark:stroke-neutral-800"
                  strokeWidth={0.5}
                />
              )
          )}

          {/* Right-axis KB line */}
          {showKbLine && kbLinePath && (
            <>
              {kbGridYs.map((g) => (
                <text
                  key={g.y}
                  x={width - paddingRight + 6}
                  y={g.y + 3}
                  textAnchor="start"
                  fontSize="8"
                  fill={ACTIVITY_PALETTE[AdminAnalyticsActivityType.KnowledgeBaseUpload].hex}
                >
                  {compactNumberFormatter.format(g.label)}
                </text>
              ))}
              <path
                d={kbLinePath}
                fill="none"
                stroke={ACTIVITY_PALETTE[AdminAnalyticsActivityType.KnowledgeBaseUpload].hex}
                strokeWidth="2"
                strokeDasharray="5 3"
                strokeLinecap="round"
              />
              {kbDocCounts.map((v, i) => (
                <circle
                  key={i}
                  cx={toX(i)}
                  cy={toYKb(v)}
                  r="2.5"
                  fill={ACTIVITY_PALETTE[AdminAnalyticsActivityType.KnowledgeBaseUpload].hex}
                />
              ))}
            </>
          )}

          {/* X-axis labels */}
          {labelIndices.map((i) => (
            <text
              key={i}
              x={toX(i)}
              y={height - 8}
              textAnchor="middle"
              fontSize="8"
              className="fill-neutral-400 dark:fill-neutral-500"
            >
              {dateFormatter.format(visiblePeriods[i].periodStart)}
            </text>
          ))}

          {/* Hover crosshair */}
          {hoverIndex !== null && (
            <g pointerEvents="none">
              <line
                x1={toX(hoverIndex)}
                x2={toX(hoverIndex)}
                y1={paddingTop}
                y2={paddingTop + innerHeight}
                className="stroke-neutral-900 dark:stroke-white"
                strokeOpacity={0.15}
                strokeDasharray="3 3"
              />
            </g>
          )}
        </svg>

        {hoverIndex !== null && (
          <ChartTooltip
            period={stacked[hoverIndex]}
            kbCumulative={showKbLine ? kbDocCounts[hoverIndex] : null}
            index={hoverIndex}
            total={visiblePeriods.length}
          />
        )}
      </div>
      {kbHistoryNote && <FilterNote className="mt-2">{kbHistoryNote}</FilterNote>}
    </ChartFrame>
  );
};

const ChartTooltip = ({
  period,
  kbCumulative,
  index,
  total
}: {
  period: {
    period: ActivityPeriod;
    total: number;
    entries: { type: AdminAnalyticsActivityType; start: number; end: number }[];
  };
  kbCumulative: number | null;
  index: number;
  total: number;
}) => {
  const alignRight = index < total / 2;
  return (
    <div
      className={`pointer-events-none absolute top-3 ${alignRight ? 'right-3' : 'left-12'} min-w-[10rem] rounded-lg bg-neutral-900/95 px-3 py-2 text-xs text-white shadow-lg ring-1 ring-white/10`}
    >
      <div className="font-semibold">{longDateFormatter.format(period.period.periodStart)}</div>
      <div className="mt-2 space-y-1">
        {AREA_STACK_ORDER.map((type) => {
          const count = period.period.counts[type] ?? 0;
          if (count === 0) return null;
          return (
            <div key={type} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: ACTIVITY_PALETTE[type].hex }}
                />
                {ACTIVITY_LABELS[type]}
              </span>
              <span className="font-semibold tabular-nums">{formatNumber(count)}</span>
            </div>
          );
        })}
        {kbCumulative !== null && (
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5">
              <svg width="10" height="4" className="shrink-0">
                <line
                  x1="0"
                  y1="2"
                  x2="10"
                  y2="2"
                  stroke={ACTIVITY_PALETTE[AdminAnalyticsActivityType.KnowledgeBaseUpload].hex}
                  strokeWidth="1.5"
                  strokeDasharray="2 1.5"
                />
              </svg>
              {ACTIVITY_LABELS[AdminAnalyticsActivityType.KnowledgeBaseUpload]}
            </span>
            <span className="font-semibold tabular-nums">{formatNumber(kbCumulative)}</span>
          </div>
        )}
      </div>
      {period.total > 0 && (
        <div className="mt-2 flex items-center justify-between border-t border-white/10 pt-2">
          <span className="text-neutral-300 dark:text-neutral-600">Period total</span>
          <span className="font-semibold tabular-nums">{formatNumber(period.total)}</span>
        </div>
      )}
    </div>
  );
};

const ChartFrame = ({
  title,
  subtitle,
  legend,
  children
}: {
  title: string;
  subtitle?: string;
  legend?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="my-0 text-sm font-semibold text-neutral-900 dark:text-white">{title}</h3>
        {subtitle && (
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{subtitle}</p>
        )}
      </div>
      {legend}
    </div>
    <div className="mt-4">{children}</div>
  </>
);

type WorkspaceMixItem = {
  key: string;
  label: string;
  count: number;
  custom?: boolean;
  collapsed?: CollapsedLegendEntry[];
};

const WorkspaceMixLegendRow = ({
  item,
  color,
  pct
}: {
  item: WorkspaceMixItem;
  color: string;
  pct: number;
}) => (
  <li className="flex items-center text-sm">
    <CollapsedLegendPopover entries={item.collapsed}>
      <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="block min-w-0">
        <span className="flex items-baseline gap-2">
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="min-w-0 truncate font-medium text-neutral-900 dark:text-white">
              {item.label}
            </span>
            {item.custom && <CustomPresetTag />}
          </span>
          <span className="shrink-0 font-semibold tabular-nums text-neutral-500 dark:text-neutral-400">
            {formatNumber(item.count)}
          </span>
        </span>
        <span className="block text-xs text-neutral-500 dark:text-neutral-400">
          {pct}% of workspaces
          {item.collapsed
            ? ` · ${item.collapsed.length} ${item.collapsed.length === 1 ? 'type' : 'types'}`
            : ''}
        </span>
      </span>
    </CollapsedLegendPopover>
  </li>
);

const WorkspaceMix = ({ report }: { report: Report }) => {
  const aggregatedBreakdown = React.useMemo(() => {
    // Summed by key, not label: a built-in and a custom preset can share a label.
    const map = new Map<string, WorkspaceMixItem>();
    for (const item of report.workspaceBreakdown) {
      const label = presetDisplayLabel(item.label, item.customPresetArchived);
      const existing = map.get(item.key);
      if (existing) {
        existing.count += item.count;
      } else {
        map.set(item.key, {
          key: item.key,
          label,
          count: item.count,
          custom: item.customPreset != null
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [report.workspaceBreakdown]);

  const top = aggregatedBreakdown.slice(0, 5);
  const rest = aggregatedBreakdown.slice(5);
  const otherCount = rest.reduce((sum, i) => sum + i.count, 0);
  // The "Other" row keeps the rows it collapsed so the legend can reveal them.
  const items: WorkspaceMixItem[] =
    otherCount > 0
      ? [...top, { key: 'other', label: 'Other', count: otherCount, collapsed: rest }]
      : top;
  const total = items.reduce((sum, i) => sum + i.count, 0);
  const hasDeletedPreset = report.workspaceBreakdown.some((i) => i.customPresetArchived);

  if (total === 0) {
    return (
      <>
        <SectionHeading title="Workspace types" subtitle="What your team is using Cara for" />
        <p className="py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">
          No workspaces in this view.
        </p>
      </>
    );
  }

  return (
    <>
      <SectionHeading title="Workspace types" subtitle="What your team is using Cara for" />
      {hasDeletedPreset && <DeletedPresetFootnote />}
      <div className="mt-4 flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
        <div className="shrink-0">
          <CategoryDonut
            total={total}
            label="workspaces"
            items={items.map((i, idx) => ({ value: i.count, color: WORKSPACE_PALETTE[idx] }))}
          />
        </div>
        <ul className="w-full min-w-0 space-y-2.5 sm:flex-1 sm:pl-6 lg:pl-10">
          {items.map((item, idx) => (
            <WorkspaceMixLegendRow
              key={item.key}
              item={item}
              color={WORKSPACE_PALETTE[idx]}
              pct={Math.round((item.count / total) * 100)}
            />
          ))}
        </ul>
      </div>
    </>
  );
};

const CategoryDonut = ({
  total,
  label,
  items,
  selectedIndex,
  onSelect,
  centerTotal,
  centerLabel
}: {
  total: number;
  label: string;
  items: { value: number; color: string }[];
  selectedIndex?: number;
  onSelect?: (index: number) => void;
  centerTotal?: number;
  centerLabel?: string;
}) => {
  // Render slices using stroke-dasharray on a circle path. Each subsequent
  // slice is rotated using stroke-dashoffset for crispness without arc math.
  // viewBox must include stroke half-width beyond the circle radius or the ring clips.
  const strokeWidth = 14;
  const viewBoxSize = 132;
  const center = viewBoxSize / 2;
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const displayTotal = centerTotal ?? total;
  const displayLabel = centerLabel ?? label;
  const interactive = onSelect != null;
  let offset = 0;
  return (
    <div className="relative size-40 sm:size-44">
      <svg viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`} className="size-full -rotate-90">
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          className="stroke-neutral-100 dark:stroke-neutral-700"
          strokeWidth={strokeWidth}
        />
        {items.map((slice, idx) => {
          const length = total === 0 ? 0 : (slice.value / total) * circumference;
          const dash = `${length} ${circumference - length}`;
          const isSelected = selectedIndex === idx;
          const dimmed =
            interactive && selectedIndex != null && selectedIndex !== idx && slice.value > 0;
          const node = (
            <circle
              key={idx}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={slice.color}
              strokeWidth={isSelected ? strokeWidth + 2 : strokeWidth}
              strokeDasharray={dash}
              strokeDashoffset={-offset}
              opacity={dimmed ? 0.35 : 1}
              className={interactive && slice.value > 0 ? 'cursor-pointer' : undefined}
              role={interactive && slice.value > 0 ? 'button' : undefined}
              tabIndex={interactive && slice.value > 0 ? 0 : undefined}
              aria-label={
                interactive && slice.value > 0
                  ? `Select slice ${idx + 1}, ${formatNumber(slice.value)}`
                  : undefined
              }
              onClick={interactive && slice.value > 0 ? () => onSelect?.(idx) : undefined}
              onKeyDown={
                interactive && slice.value > 0
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect?.(idx);
                      }
                    }
                  : undefined
              }
            />
          );
          offset += length;
          return node;
        })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="text-lg font-semibold text-neutral-900 dark:text-white sm:text-xl">
          {formatNumber(displayTotal)}
        </div>
        <div className="text-[10px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
          {displayLabel}
        </div>
      </div>
    </div>
  );
};

// Keys match the typeLabel strings returned by the backend
// (pkg/db/analytics.go analyticsTemplateArtifactQuery + buildTemplateBreakdownFromArtifactRows).
type TemplateTypeConfig = {
  label: string;
  color: TemplateBadgeColor;
  libraryKey: 'customFormCount' | 'htmlTemplateCount' | 'wordTemplateCount' | null;
  libraryLabel: string | null;
};
const TEMPLATE_TYPES: Record<string, TemplateTypeConfig> = {
  'Template form': { label: 'ACORD forms', color: 'emerald', libraryKey: null, libraryLabel: null },
  'Custom form': {
    label: 'Custom forms',
    color: 'sky',
    libraryKey: 'customFormCount',
    libraryLabel: 'custom forms in library'
  },
  Document: {
    label: 'Documents',
    color: 'blue',
    libraryKey: 'htmlTemplateCount',
    libraryLabel: 'documents in library'
  },
  'Excel file': {
    label: 'Excel files',
    color: 'amber',
    libraryKey: null,
    libraryLabel: null
  },
  'Word document': {
    label: 'Word documents',
    color: 'amber',
    libraryKey: 'wordTemplateCount',
    libraryLabel: 'Word templates in library'
  }
};

// ─── Workspace insights (replaces "Top tools used") ─────────────────────────
//
// Three KPI tiles describe how Cara works inside a typical workspace
// (intensity, file pages, output). A small histogram below shows the shape of
// the tool-call distribution so admins can spot lopsided usage patterns
// (e.g. most workspaces with 0 tool calls, a long tail of heavy ones).

/** Display label for tool-call histogram buckets (also maps legacy cached API values). */
function formatToolCallDistributionLabel(label: string): string {
  if (label === '0') {
    return 'Message Only';
  }
  return label;
}

const WorkspaceInsightsCard = ({ report }: { report: Report }) => {
  const stats = report.workspaceStats;
  const distMax = Math.max(1, ...stats.toolCallDistribution.map((b) => b.count));
  const totalDistribution = stats.toolCallDistribution.reduce((s, b) => s + b.count, 0);

  if (stats.totalWorkspaces === 0) {
    return (
      <>
        <SectionHeading
          title="Workspace insights"
          subtitle="How much work Cara does inside a typical workspace"
        />
        <p className="py-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
          No workspaces in this view yet.
        </p>
      </>
    );
  }

  return (
    <>
      <SectionHeading
        title="Workspace insights"
        subtitle="How much work Cara does inside a typical workspace"
      />
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiTile
          label="AI Actions per workspace"
          value={formatDecimal(stats.avgToolCallsPerWorkspace)}
        />
        <KpiTile
          label="Pages reviewed per workspace"
          value={formatDecimal(stats.avgPagesPerWorkspace)}
        />
        <KpiTile
          label="Generated docs per workspace"
          value={formatDecimal(stats.avgArtifactsPerWorkspace)}
        />
      </div>

      <div className="mt-5">
        <div className="mb-1.5 flex items-baseline justify-between">
          <span className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            AI Actions per workspace
          </span>
          <span className="text-[11px] text-neutral-400 dark:text-neutral-500">
            distribution of {formatNumber(totalDistribution)} workspaces
          </span>
        </div>
        <div className="flex h-28 items-end gap-1.5">
          {stats.toolCallDistribution.map((bucket) => {
            const heightPct = Math.round((bucket.count / distMax) * 100);
            const bucketLabel = formatToolCallDistributionLabel(bucket.label);
            return (
              <div key={bucket.label} className="flex flex-1 flex-col items-center gap-1">
                <div className="flex h-20 w-full items-end justify-center">
                  <div
                    className="w-full rounded-t bg-primary-500/85 transition-[height] duration-200"
                    style={{ height: bucket.count === 0 ? '2px' : `${Math.max(4, heightPct)}%` }}
                    title={`${bucketLabel}: ${formatNumber(bucket.count)} workspace${bucket.count === 1 ? '' : 's'}`}
                  />
                </div>
                <div className="text-[10px] font-medium text-neutral-700 dark:text-neutral-300 tabular-nums">
                  {formatNumber(bucket.count)}
                </div>
                <div
                  className="px-0.5 text-center text-[9px] leading-tight text-neutral-500 dark:text-neutral-400"
                  title={bucketLabel}
                >
                  {bucketLabel}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ─── Template usage breakdown (full-width tiled section) ─────────────────────

const TemplateBreakdownSection = ({
  report,
  libraryNote
}: {
  report: Report;
  libraryNote: string | undefined;
}) => {
  const grouped = React.useMemo(() => {
    const buckets = new Map<string, typeof report.templateBreakdown>();
    for (const item of report.templateBreakdown) {
      const list = buckets.get(item.templateType) ?? [];
      list.push(item);
      buckets.set(item.templateType, list);
    }
    return [...buckets.entries()]
      .map(([type, items]) => ({
        type,
        items: items.slice(0, 5),
        total: items.reduce((sum, i) => sum + i.count, 0)
      }))
      .sort((a, b) => b.total - a.total);
  }, [report.templateBreakdown]);

  const grandTotal = grouped.reduce((s, g) => s + g.total, 0);

  if (grandTotal === 0) return null;

  return (
    <div>
      <div className="mb-3 flex items-end justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
            Generated templates and forms
          </h3>
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
            Most generated outputs by type ·{' '}
            <span className="font-medium text-neutral-700 dark:text-neutral-300">
              {formatNumber(grandTotal)} total generations
            </span>
          </p>
          {libraryNote && <FilterNote className="mt-0.5">{libraryNote}</FilterNote>}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {grouped.map((group) => (
          <TemplateTypeCard
            key={group.type}
            group={group}
            grandTotal={grandTotal}
            library={libraryNote ? null : report.templateLibrary}
          />
        ))}
      </div>
    </div>
  );
};

const TemplateTypeCard = ({
  group,
  grandTotal,
  library
}: {
  group: { type: string; items: { label: string; count: number }[]; total: number };
  grandTotal: number;
  library: Report['templateLibrary'] | null;
}) => {
  const typeConfig = TEMPLATE_TYPES[group.type];
  const label = typeConfig?.label ?? titleCase(group.type);
  const color = typeConfig?.color ?? 'zinc';
  const pctOfAll = Math.round((group.total / grandTotal) * 100);
  const libCount = library && typeConfig?.libraryKey ? library[typeConfig.libraryKey] : null;
  const libLabel = typeConfig?.libraryLabel;
  const progressVariant = TEMPLATE_BADGE_TO_PROGRESS[color];

  return (
    <div className="flex flex-col rounded-xl bg-white dark:bg-neutral-800 px-4 py-4 shadow">
      <div className="flex items-center justify-between gap-2">
        <Badge color={color}>{label}</Badge>
        <span className="text-xs font-medium tabular-nums text-neutral-500 dark:text-neutral-400">
          {formatNumber(group.total)}
        </span>
      </div>

      <MetricProgressBar
        value={pctOfAll}
        variant={progressVariant}
        size="sm"
        className="mt-2 w-full"
      />
      <p className="mt-1 text-[11px] text-neutral-400 dark:text-neutral-500">
        {pctOfAll}% of all generated
      </p>

      <ul className="mt-3 flex-1 space-y-2">
        {group.items.map((item) => (
          <li key={`${group.type}-${item.label}`}>
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="min-w-0 truncate text-neutral-700 dark:text-neutral-300">
                {item.label}
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-neutral-900 dark:text-white">
                {formatNumber(item.count)}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {libCount !== null && (
        <div className="mt-4 flex items-center gap-2 border-t border-neutral-100 dark:border-neutral-700 pt-3 text-xs text-neutral-500 dark:text-neutral-400">
          <span className="font-semibold tabular-nums text-neutral-700 dark:text-neutral-300">
            {formatNumber(libCount)}
          </span>
          {libLabel}
        </div>
      )}
    </div>
  );
};

const UserActivityPanel = ({
  report,
  adoptionNote
}: {
  report: Report;
  adoptionNote: string | undefined;
}) => {
  const [sortKey, setSortKey] = React.useState<UserActivitySortKey>('user');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const columns = React.useMemo(() => getUserActivityColumns(), []);

  return (
    <>
      <SectionHeading
        title="User activity"
        subtitle="Workspace, email, document, library, and login activity by teammate"
      />
      {adoptionNote ? (
        <EmptyMessage>{adoptionNote}</EmptyMessage>
      ) : report.activeUsers.length === 0 ? (
        <p className="py-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
          No team members to show.
        </p>
      ) : (
        <div className="mt-3 -mx-6">
          <DataTable
            data={report.activeUsers}
            columns={columns}
            keyExtractor={(user) => user.agencyUserId}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={(key, direction) => {
              setSortKey(key);
              setSortDirection(direction);
            }}
            sortFn={sortUserActivity}
            stickyHeader
            scrollable
            maxHeight="28rem"
            tableClassName="!mx-0 whitespace-normal [--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
          />
        </div>
      )}
    </>
  );
};

type KbRankedUser = {
  agencyUserId: string;
  agencyUserName: string;
  agencyUserEmail: string;
  count: number;
};

const KbRankedList = ({ users, emptyMessage }: { users: KbRankedUser[]; emptyMessage: string }) => {
  if (users.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      {users.map((user, idx) => (
        <div key={user.agencyUserId} className="flex items-center gap-3">
          <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-700 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400">
            {idx + 1}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-neutral-900 dark:text-white">
                  {user.agencyUserName}
                </div>
                <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                  {user.agencyUserEmail}
                </div>
              </div>
              <div className="shrink-0 text-sm font-semibold tabular-nums text-neutral-900 dark:text-white">
                {formatNumber(user.count)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Full-width panel: who builds the library vs. who searches it (behavior + adoption),
// without restating library size already shown in the hero Knowledge stat card.
const KnowledgeBaseInsightsPanel = ({
  report,
  uploadsNote
}: {
  report: Report;
  uploadsNote: string | undefined;
}) => {
  const contributors = React.useMemo(
    () =>
      [...report.userUsage]
        .filter((u) => u.knowledgeBaseUploadCount > 0)
        .sort((a, b) => b.knowledgeBaseUploadCount - a.knowledgeBaseUploadCount),
    [report.userUsage]
  );
  const topContributors = contributors.slice(0, 5);

  const search = report.kbSearchUsage;
  const topSearchers = search.perUser.slice(0, 5);

  const hasContributors = contributors.length > 0;
  const hasSearchers = search.searcherCount > 0;

  if (!hasContributors && !hasSearchers && !uploadsNote) {
    return (
      <p className="py-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
        No knowledge base activity in this view yet.
      </p>
    );
  }

  const searchesPerSearcher =
    search.searcherCount === 0
      ? '—'
      : formatNumber(Math.round(search.totalSearches / search.searcherCount));

  const contributorRows: KbRankedUser[] = topContributors.map((u) => ({
    agencyUserId: u.agencyUserId,
    agencyUserName: u.agencyUserName,
    agencyUserEmail: u.agencyUserEmail,
    count: u.knowledgeBaseUploadCount
  }));

  const searcherRows: KbRankedUser[] = topSearchers.map((u) => ({
    agencyUserId: u.agencyUserId,
    agencyUserName: u.agencyUserName,
    agencyUserEmail: u.agencyUserEmail,
    count: u.searchCount
  }));

  return (
    <>
      <div
        className={clsx(
          'grid grid-cols-1 gap-3',
          uploadsNote ? 'sm:grid-cols-2' : 'sm:grid-cols-3'
        )}
      >
        {!uploadsNote && (
          <KpiTile
            label="Documents uploaded"
            value={formatNumber(report.totals.totalKnowledgeBaseUploads)}
            detail={`${formatNumber(report.totals.totalKnowledgeBasePages)} pages indexed`}
          />
        )}
        <KpiTile
          label="Knowledge base searches"
          value={formatNumber(search.totalSearches)}
          detail="total search tool uses in this period"
        />
        <KpiTile
          label="Searches per active searcher"
          value={searchesPerSearcher}
          detail={
            search.searcherCount === 0
              ? 'no searchers in this view'
              : `average among ${formatNumber(search.searcherCount)} teammate${search.searcherCount === 1 ? '' : 's'} who searched`
          }
        />
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-2 lg:gap-10">
        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Documents added to library
          </h4>
          {hasContributors && (
            <p className="mt-1 text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400">
              Total number of documents added to the knowledge base in this period.
            </p>
          )}
          {uploadsNote ? (
            <EmptyMessage>{uploadsNote}</EmptyMessage>
          ) : (
            <KbRankedList users={contributorRows} emptyMessage="No uploads in this view." />
          )}
        </div>

        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Knowledge base searches
          </h4>
          {hasSearchers && (
            <p className="mt-1 text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400">
              Total number of knowledge base searches in this period.
            </p>
          )}
          <KbRankedList
            users={searcherRows}
            emptyMessage="Nobody has searched the knowledge base in this view yet."
          />
        </div>
      </div>
    </>
  );
};

function buildActivityPeriods(report: Report): ActivityPeriod[] {
  const byPeriod = new Map<string, ActivityPeriod>();
  for (const bucket of report.activityOverTime) {
    const periodStart = toDate(bucket.periodStart);
    const key = periodStart.toISOString();
    const period = byPeriod.get(key) ?? { periodStart, total: 0, counts: {} };
    period.counts[bucket.activityType] = (period.counts[bucket.activityType] ?? 0) + bucket.count;
    period.total += bucket.count;
    byPeriod.set(key, period);
  }
  return [...byPeriod.values()].sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime());
}

function computeTrends(periods: ActivityPeriod[]) {
  // Compare totals from the second half of the range against the first half
  // so we can show direction + magnitude of change without an extra query.
  const sumActivity = (subset: ActivityPeriod[], type: AdminAnalyticsActivityType) =>
    subset.reduce((sum, period) => sum + (period.counts[type] ?? 0), 0);

  const trendFor = (type: AdminAnalyticsActivityType): Trend => {
    if (periods.length < 2) {
      return { current: 0, previous: 0, deltaPct: null };
    }
    const mid = Math.floor(periods.length / 2);
    const previous = sumActivity(periods.slice(0, mid), type);
    const current = sumActivity(periods.slice(mid), type);
    if (previous === 0 && current === 0) {
      return { current, previous, deltaPct: null };
    }
    if (previous === 0) {
      return { current, previous, deltaPct: 100 };
    }
    const deltaPct = Math.round(((current - previous) / previous) * 100);
    return { current, previous, deltaPct };
  };

  return {
    workspace: trendFor(AdminAnalyticsActivityType.Workspace),
    call: trendFor(AdminAnalyticsActivityType.Call),
    email: trendFor(AdminAnalyticsActivityType.Email)
  };
}

function formatDecimal(value: number): string {
  if (!Number.isFinite(value)) return '0';
  // Whole numbers render without a trailing ".0"; otherwise show one decimal
  // so KPI tiles stay compact ("2.4" rather than "2.45718").
  return value % 1 === 0 ? formatNumber(value) : value.toFixed(1);
}

function formatCurrency(value: number): string {
  return currencyFormatter.format(Math.round(value));
}

function formatTimeSaved(totalMins: number): string {
  if (totalMins === 0) return '0 min';
  if (totalMins < 60) return `${formatNumber(totalMins)} min`;
  const hours = totalMins / 60;
  if (hours < 10) {
    return hours % 1 === 0 ? `${formatNumber(hours)}h` : `${hours.toFixed(1)}h`;
  }
  return `${formatNumber(Math.round(hours))}h`;
}

function toDate(value: Date | string) {
  return value instanceof Date ? value : new Date(value);
}
